import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtMoney0, fmtDate } from "../lib/format";
import { Modal, Field, Badge, Spinner, EmptyState, Confirm, Tabs } from "../components/ui";
import { SearchSelect, orgOptions } from "../components/SearchSelect";

const CONTRAT_STATUTS = ["Actif", "EnRenouvellement", "POC", "Terminé", "Expire", "Perdu"];
const TRANCHES = ["J-0", "J-30", "J-60", "J-90", "J-120"];

function ContratForm({ contrat, orgs, produits, onSave, onClose, mgr, allContrats, onOpenContrat }: any) {
  const [f, setF] = useState<any>({ statut: "Actif", devise: "EUR", ...contrat });
  const [err, setErr] = useState<string | null>(null);
  const produit = produits.find((p: any) => p.id === f.produit_id);
  const idEd = (f.id_editeur || "").trim().toLowerCase();
  const dupes = idEd ? (allContrats || []).filter((c: any) => c.id !== f.id && (c.id_editeur || "").trim().toLowerCase() === idEd) : [];
  return (
    <Modal title={f.id ? `Contrat ${f.reference}` : "Nouveau contrat"} onClose={onClose} wide testId="contrat-modal">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Field label="Client">
          <SearchSelect testId="contrat-client-select" options={orgOptions(orgs.filter((o: any) => o.type === "client" || o.type === "revendeur"))} value={f.client_id || null} onChange={(v) => setF({ ...f, client_id: v })} />
        </Field>
        <Field label="Revendeur">
          <SearchSelect testId="contrat-revendeur-select" options={orgOptions(orgs.filter((o: any) => o.type === "revendeur"))} value={f.revendeur_id || null} onChange={(v) => setF({ ...f, revendeur_id: v })} />
        </Field>
        <Field label="Produit">
          <select data-testid="contrat-produit-select" className="nx-input" value={f.produit_id || ""} onChange={(e) => setF({ ...f, produit_id: e.target.value || null, sous_produit_id: null })}>
            <option value="">—</option>
            {produits.map((p: any) => <option key={p.id} value={p.id}>{p.nom}</option>)}
          </select>
        </Field>
        <Field label="Sous-produit">
          <select data-testid="contrat-sousproduit-select" className="nx-input" value={f.sous_produit_id || ""} onChange={(e) => {
            const sp = (produit?.sous_produits || []).find((x: any) => x.id === e.target.value);
            setF({ ...f, sous_produit_id: e.target.value || null, sous_produit_nom: sp?.nom || null });
          }}>
            <option value="">—</option>
            {(produit?.sous_produits || []).map((sp: any) => <option key={sp.id} value={sp.id}>{sp.nom}</option>)}
          </select>
        </Field>
        <Field label="Nombre de licences">
          <input data-testid="contrat-nb-input" type="number" className="nx-input" value={f.nb_licences ?? ""} onChange={(e) => setF({ ...f, nb_licences: e.target.value === "" ? null : +e.target.value })} />
        </Field>
        <Field label="Montant">
          <input data-testid="contrat-montant-input" type="number" step="0.01" className="nx-input" value={f.montant ?? ""} onChange={(e) => setF({ ...f, montant: e.target.value === "" ? null : +e.target.value })} />
        </Field>
        {mgr && (
          <Field label="Marge">
            <input data-testid="contrat-marge-input" type="number" step="0.01" className="nx-input" value={f.marge ?? ""} onChange={(e) => setF({ ...f, marge: e.target.value === "" ? null : +e.target.value })} />
          </Field>
        )}
        <Field label="Statut">
          <select data-testid="contrat-statut-select" className="nx-input" value={f.statut} onChange={(e) => setF({ ...f, statut: e.target.value })}>
            {CONTRAT_STATUTS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Échéance">
          <input data-testid="contrat-echeance-input" type="date" className="nx-input" value={(f.date_echeance || "").slice(0, 10)} onChange={(e) => setF({ ...f, date_echeance: e.target.value || null })} />
        </Field>
        <Field label="Renouvelé">
          <select className="nx-input" value={f.renouvele || ""} onChange={(e) => setF({ ...f, renouvele: e.target.value || null })}>
            <option value="">—</option>
            <option value="oui">Oui</option>
            <option value="non">Non</option>
          </select>
        </Field>
        <Field label="Partenaire">
          <SearchSelect testId="contrat-partenaire-select" options={orgOptions(orgs.filter((o: any) => o.type === "revendeur"))} value={f.partenaire_id || null} onChange={(v) => setF({ ...f, partenaire_id: v })} />
        </Field>
        <Field label="ID éditeur">
          <input data-testid="contrat-idediteur-input" className="nx-input" value={f.id_editeur || ""} onChange={(e) => setF({ ...f, id_editeur: e.target.value })} placeholder="Réf. éditeur (optionnel)" />
        </Field>
        {f.statut === "POC" && (
          <>
            <Field label="Début POC">
              <input type="date" className="nx-input" value={(f.poc_debut || "").slice(0, 10)} onChange={(e) => setF({ ...f, poc_debut: e.target.value || null })} />
            </Field>
            <Field label="Fin POC">
              <input type="date" className="nx-input" value={(f.poc_fin || "").slice(0, 10)} onChange={(e) => setF({ ...f, poc_fin: e.target.value || null })} />
            </Field>
            <Field label="Statut POC">
              <select className="nx-input" value={f.poc_statut || ""} onChange={(e) => setF({ ...f, poc_statut: e.target.value })}>
                <option value="">—</option>
                {["En cours", "Attente", "Réussi", "Échec"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </>
        )}
      </div>
      {dupes.length > 0 && (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber" data-testid="contrat-idediteur-warning">
          Attention : cet ID éditeur est déjà utilisé par{" "}
          {dupes.map((c: any, i: number) => (
            <React.Fragment key={c.id}>
              {i > 0 ? ", " : ""}
              <button type="button" className="font-semibold underline" data-testid={`contrat-idediteur-dup-${c.reference || c.id}`} onClick={() => onOpenContrat(c)}>
                {c.reference || "contrat"}{c.client_nom ? ` (${c.client_nom})` : ""}
              </button>
            </React.Fragment>
          ))}
          . Les doublons restent autorisés.
        </p>
      )}
      {err && <p className="mt-2 text-sm text-danger">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="nx-btn-ghost" onClick={onClose}>Annuler</button>
        <button data-testid="contrat-save-btn" className="nx-btn-primary" onClick={async () => { setErr(null); try { await onSave(f); } catch (e) { setErr(apiError(e)); } }}>Enregistrer</button>
      </div>
    </Modal>
  );
}

function KpaxForm({ env, orgs, onSave, onClose }: any) {
  const [f, setF] = useState<any>({ regime: "production", machines: 0, ...env });
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title={f.id ? `KPAX — ${f.nom}` : "Nouvel environnement KPAX"} onClose={onClose} testId="kpax-modal">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nom">
          <input data-testid="kpax-nom-input" className="nx-input" value={f.nom || ""} onChange={(e) => setF({ ...f, nom: e.target.value })} />
        </Field>
        <Field label="URL">
          <input className="nx-input" value={f.url || ""} onChange={(e) => setF({ ...f, url: e.target.value })} />
        </Field>
        <Field label="Client">
          <SearchSelect testId="kpax-client-select" options={orgOptions(orgs.filter((o: any) => o.type === "client" || o.type === "revendeur"))} value={f.client_id || null} onChange={(v) => setF({ ...f, client_id: v })} />
        </Field>
        <Field label="Revendeur">
          <SearchSelect testId="kpax-revendeur-select" options={orgOptions(orgs.filter((o: any) => o.type === "revendeur"))} value={f.revendeur_id || null} onChange={(v) => setF({ ...f, revendeur_id: v })} />
        </Field>
        <Field label="Régime commercial">
          <select data-testid="kpax-regime-select" className="nx-input" value={f.regime} onChange={(e) => setF({ ...f, regime: e.target.value })}>
            <option value="production">Production</option>
            <option value="evaluation">Évaluation</option>
            <option value="nfr">NFR</option>
          </select>
        </Field>
        <Field label="Machines">
          <input type="number" className="nx-input" value={f.machines ?? 0} onChange={(e) => setF({ ...f, machines: +e.target.value })} />
        </Field>
        <Field label="Cycle de facturation">
          <select className="nx-input" value={f.cycle || ""} onChange={(e) => setF({ ...f, cycle: e.target.value || null })}>
            <option value="">—</option>
            <option value="mensuel">Mensuel</option>
            <option value="trimestriel">Trimestriel</option>
            <option value="annuel">Annuel</option>
          </select>
        </Field>
        <Field label="Prochaine échéance">
          <input type="date" className="nx-input" value={(f.prochaine_facture || "").slice(0, 10)} onChange={(e) => setF({ ...f, prochaine_facture: e.target.value || null })} />
        </Field>
      </div>
      {err && <p className="mt-2 text-sm text-danger">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="nx-btn-ghost" onClick={onClose}>Annuler</button>
        <button data-testid="kpax-save-btn" className="nx-btn-primary" disabled={!f.nom} onClick={async () => { setErr(null); try { await onSave(f); } catch (e) { setErr(apiError(e)); } }}>Enregistrer</button>
      </div>
    </Modal>
  );
}

function KpaxDetail({ env, onClose, reload, mgr }: any) {
  const [compteur, setCompteur] = useState("");
  const addReleve = async () => {
    if (!compteur) return;
    await api.post(`/kpax/${env.id}/releves`, { compteur: +compteur });
    setCompteur("");
    reload();
    onClose();
  };
  return (
    <Modal title={`KPAX — ${env.nom}`} onClose={onClose} testId="kpax-detail-modal">
      <div className="mb-3 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-lg bg-soft px-3 py-1.5">Revenu : <b>{fmtMoney0(env.calc?.revenu)}</b></div>
        {mgr && <div className="rounded-lg bg-soft px-3 py-1.5">Coût : <b>{fmtMoney0(env.calc?.cout)}</b></div>}
        {mgr && <div className="rounded-lg bg-soft px-3 py-1.5">Marge : <b>{fmtMoney0(env.calc?.marge)}</b></div>}
        <div className="rounded-lg bg-soft px-3 py-1.5">Tendance : <b>{env.calc?.tendance != null ? `${env.calc.tendance > 0 ? "+" : ""}${env.calc.tendance} %` : "—"}</b></div>
      </div>
      <h3 className="mb-2 text-sm font-bold text-green">Relevés de compteurs</h3>
      <ul className="mb-3 max-h-40 space-y-1 overflow-y-auto">
        {(env.releves || []).map((r: any) => (
          <li key={r.id} className="flex justify-between rounded-lg bg-soft px-3 py-1 text-sm">
            <span>{fmtDate(r.date)}</span>
            <b>{r.compteur.toLocaleString("fr-FR")}</b>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input data-testid="kpax-releve-input" type="number" className="nx-input" placeholder="Nouveau relevé…" value={compteur} onChange={(e) => setCompteur(e.target.value)} />
        <button data-testid="kpax-releve-add-btn" className="nx-btn-primary" onClick={addReleve}>Ajouter</button>
      </div>
    </Modal>
  );
}

export default function Recurrent() {
  const { user } = useAuth();
  const mgr = user?.role === "manager";
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "papercut");
  const [contrats, setContrats] = useState<any[] | null>(null);
  const [kpax, setKpax] = useState<any[]>([]);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [produits, setProduits] = useState<any[]>([]);
  const [editContrat, setEditContrat] = useState<any>(null);
  const [editKpax, setEditKpax] = useState<any>(null);
  const [detailKpax, setDetailKpax] = useState<any>(null);
  const [del, setDel] = useState<any>(null);
  const [filtreTranche, setFiltreTranche] = useState<string | null>(null);
  const [paysSort, setPaysSort] = useState<"asc" | "desc" | null>(null);

  const togglePaysSort = () => setPaysSort((s) => (s === null ? "asc" : s === "asc" ? "desc" : null));
  const byPays = (list: any[]) => {
    if (!paysSort) return list;
    const s = [...list].sort((a, b) => (a.client_pays || "").localeCompare(b.client_pays || "", "fr"));
    return paysSort === "desc" ? s.reverse() : s;
  };
  const PaysHeader = () => (
    <th className="cursor-pointer select-none" data-testid="recurrent-pays-sort" onClick={togglePaysSort}>
      Pays{paysSort === "asc" ? " ▲" : paysSort === "desc" ? " ▼" : ""}
    </th>
  );

  const load = () => {
    api.get("/contrats").then((r) => setContrats(r.data));
    api.get("/kpax").then((r) => setKpax(r.data));
  };
  useEffect(() => {
    load();
    api.get("/organisations").then((r) => setOrgs(r.data));
    api.get("/produits").then((r) => setProduits(r.data));
    if (params.get("new")) setEditContrat({ statut: "Actif" });
  }, [params]);

  const visible = useMemo(() => {
    if (!contrats) return [];
    let list = contrats.filter((c) => c.statut !== "POC");
    if (tab === "papercut") list = list.filter((c) => c.produit_nom === "PaperCut");
    if (tab === "autres") list = list.filter((c) => c.produit_nom !== "PaperCut");
    if (filtreTranche) list = list.filter((c) => c.tranche === filtreTranche);
    return list;
  }, [contrats, tab, filtreTranche]);

  if (!contrats) return <Spinner />;

  const sousProduitsCards = Array.from(
    visible.reduce((m, c) => {
      const key = c.sous_produit_nom || c.sous_produit_ref_nom || "Autre";
      const e = m.get(key) || { nb: 0, montant: 0 };
      e.nb += 1;
      e.montant += +c.montant || 0;
      m.set(key, e);
      return m;
    }, new Map<string, any>())
  );

  const saveContrat = async (f: any) => {
    if (f.id) await api.patch(`/contrats/${f.id}`, f);
    else await api.post("/contrats", f);
    setEditContrat(null);
    load();
  };
  const saveKpax = async (f: any) => {
    if (f.id) await api.patch(`/kpax/${f.id}`, f);
    else await api.post("/kpax", f);
    setEditKpax(null);
    load();
  };

  return (
    <div data-testid="recurrent-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-green">Récurrent</h1>
        <div className="flex gap-2">
          <button data-testid="contrat-new-btn" className="nx-btn-primary" onClick={() => setEditContrat({ statut: "Actif" })}>Nouveau contrat</button>
          <button data-testid="kpax-new-btn" className="nx-btn-ghost" onClick={() => setEditKpax({})}>Nouvel env. KPAX</button>
        </div>
      </div>
      <Tabs
        tabs={[
          { key: "papercut", label: "PaperCut", count: contrats.filter((c) => c.produit_nom === "PaperCut" && c.statut !== "POC").length },
          { key: "autres", label: "Autres", count: contrats.filter((c) => c.produit_nom !== "PaperCut" && c.statut !== "POC").length },
          { key: "kpax", label: "KPAX", count: kpax.length },
        ]}
        active={tab}
        onChange={(k) => { setTab(k); setFiltreTranche(null); }}
      />

      {tab !== "kpax" && (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            {sousProduitsCards.map(([nom, v]: any) => (
              <div key={nom} className="nx-card p-3">
                <p className="text-xs font-bold uppercase text-muted">{nom}</p>
                <p className="text-lg font-extrabold text-green">{v.nb} <span className="text-sm font-semibold text-muted">· {fmtMoney0(v.montant)}</span></p>
              </div>
            ))}
          </div>
          <div className="mb-3 flex gap-2">
            {TRANCHES.map((t) => {
              const nb = contrats.filter((c) => c.statut !== "POC" && c.tranche === t).length;
              return (
                <button
                  key={t}
                  data-testid={`tranche-btn-${t}`}
                  onClick={() => setFiltreTranche(filtreTranche === t ? null : t)}
                  className={`flex-1 rounded-lg border px-2 py-1.5 text-center transition-colors ${filtreTranche === t ? "border-green bg-green text-white" : nb ? "border-lime bg-lime-soft" : "border-line bg-white"}`}
                >
                  <span className="block text-[11px] font-bold">{t}</span>
                  <span className="text-lg font-extrabold">{nb}</span>
                </button>
              );
            })}
          </div>
          <div className="nx-card overflow-x-auto">
            {visible.length === 0 ? <EmptyState message="Aucun contrat" /> : (
              <table className="nx-table">
                <thead><tr><th>Réf.</th><th>Client</th><PaysHeader /><th>Revendeur</th><th>Produit</th><th>Sous-produit</th><th>ID éditeur</th><th className="text-right">Licences</th><th className="text-right">Montant</th>{mgr && <th className="text-right">Marge</th>}<th>Statut</th><th>Échéance</th><th>Renouvelé</th><th className="text-right">Actions</th></tr></thead>
                <tbody>
                  {byPays(visible).map((c) => (
                    <tr key={c.id} data-testid={`contrat-row-${c.reference}`}>
                      <td className="font-semibold">{c.reference}</td>
                      <td>{c.client_nom}</td>
                      <td data-testid={`contrat-pays-cell-${c.reference}`}>{c.client_pays || "—"}</td>
                      <td>{c.revendeur_nom}</td>
                      <td>{c.produit_nom}</td>
                      <td>{c.sous_produit_nom || c.sous_produit_ref_nom}</td>
                      <td className="text-xs" data-testid={`contrat-idediteur-cell-${c.reference}`}>{c.id_editeur || "—"}</td>
                      <td className="text-right">{c.nb_licences}</td>
                      <td className="text-right font-semibold">{fmtMoney0(c.montant, c.devise)}</td>
                      {mgr && <td className="text-right">{fmtMoney0(c.marge)}</td>}
                      <td><Badge className={c.statut === "Actif" ? "bg-lime-soft text-green" : c.statut === "EnRenouvellement" ? "bg-amber-50 text-amber" : "bg-gray-100 text-muted"}>{c.statut}</Badge></td>
                      <td>{fmtDate(c.date_echeance)} {c.tranche && <Badge className={c.tranche === "J-0" ? "bg-red-50 text-danger" : "bg-soft text-green"}>{c.tranche}</Badge>}</td>
                      <td>{c.renouvele || "—"}</td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <button data-testid={`contrat-edit-btn-${c.reference}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => setEditContrat({ ...c })}>Éditer</button>
                          <button data-testid={`contrat-delete-btn-${c.reference}`} className="nx-btn-danger !px-2 !py-1 text-xs" onClick={() => setDel({ kind: "contrat", item: c })}>Supprimer</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === "kpax" && (
        <div className="nx-card overflow-x-auto">
          {kpax.length === 0 ? <EmptyState message="Aucun environnement KPAX" /> : (
            <table className="nx-table">
              <thead><tr><th>Nom</th><th>URL</th><th>Client</th><PaysHeader /><th>Régime</th><th className="text-right">Machines</th><th>Cycle</th><th>Prochaine échéance</th><th className="text-right">Revenu</th>{mgr && <th className="text-right">Marge</th>}<th>Tendance</th><th>Facturation</th><th className="text-right">Actions</th></tr></thead>
              <tbody>
                {byPays(kpax).map((k) => (
                  <tr key={k.id} data-testid={`kpax-row-${k.id}`}>
                    <td><button className="font-bold text-green underline-offset-2 hover:underline" onClick={() => setDetailKpax(k)}>{k.nom}</button></td>
                    <td className="text-xs text-muted">{k.url}</td>
                    <td>{k.client_nom}</td>
                    <td data-testid={`kpax-pays-cell-${k.id}`}>{k.client_pays || "—"}</td>
                    <td><Badge className={k.regime === "production" ? "bg-lime-soft text-green" : "bg-amber-50 text-amber"}>{k.regime}</Badge></td>
                    <td className="text-right">{k.machines}</td>
                    <td>{k.cycle || "—"}</td>
                    <td>{fmtDate(k.prochaine_facture)}</td>
                    <td className="text-right font-semibold">{fmtMoney0(k.calc?.revenu)}</td>
                    {mgr && <td className="text-right">{fmtMoney0(k.calc?.marge)}</td>}
                    <td>{k.calc?.tendance != null ? <span className={k.calc.tendance >= 0 ? "text-green" : "text-danger"}>{k.calc.tendance > 0 ? "+" : ""}{k.calc.tendance} %</span> : "—"}</td>
                    <td>
                      {k.facture && <Badge className="bg-green text-white" testId={`kpax-badge-facture-${k.id}`}>Facturé</Badge>}
                      {k.a_facturer && <Badge className="bg-amber-50 text-amber" testId={`kpax-badge-afacturer-${k.id}`}>À facturer</Badge>}
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1.5">
                        {k.a_facturer && (
                          <button data-testid={`kpax-facturer-btn-${k.id}`} className="nx-btn-lime !px-2 !py-1 text-xs" onClick={async () => { await api.post(`/kpax/${k.id}/facturer`); load(); }}>
                            Marquer facturé
                          </button>
                        )}
                        {k.facture && (
                          <button data-testid={`kpax-annuler-btn-${k.id}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={async () => { await api.post(`/kpax/${k.id}/facturer?annuler=true`); load(); }}>
                            Annuler
                          </button>
                        )}
                        <button data-testid={`kpax-edit-btn-${k.id}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => setEditKpax({ ...k })}>Éditer</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {editContrat && <ContratForm key={editContrat.id || "new"} contrat={editContrat} orgs={orgs} produits={produits} mgr={mgr} allContrats={contrats} onOpenContrat={(c: any) => setEditContrat({ ...c })} onSave={saveContrat} onClose={() => setEditContrat(null)} />}
      {editKpax && <KpaxForm env={editKpax} orgs={orgs} onSave={saveKpax} onClose={() => setEditKpax(null)} />}
      {detailKpax && <KpaxDetail env={detailKpax} mgr={mgr} reload={load} onClose={() => setDetailKpax(null)} />}
      {del && (
        <Confirm
          message={`Supprimer ${del.kind === "contrat" ? `le contrat ${del.item.reference}` : `l'environnement ${del.item.nom}`} ?`}
          onConfirm={async () => {
            await api.delete(del.kind === "contrat" ? `/contrats/${del.item.id}` : `/kpax/${del.item.id}`);
            setDel(null);
            load();
          }}
          onCancel={() => setDel(null)}
        />
      )}
    </div>
  );
}
